import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import type {MiniStatusIconProps} from './MiniStatusIcon'
import {MiniMergeStatusBoundary} from './MiniMergeStatusBoundary'

import {
  isSuccessStatus,
  type CombinedState,
  type StatusRollup,
} from '@github-ui/mergebox/page-data/payloads/status-checks'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import {useMergeabilityData} from '../use-mergeability-data'

export type ChecksMiniStatusProps = {
  statusRollup: StatusRollup
}

const iconTypeByCombinedState: Record<CombinedState, MiniStatusIconProps['type']> = {
  PASSED: 'success',
  PENDING: 'warning',
  PENDING_APPROVAL: 'warning',
  PENDING_CONFLICTS: 'warning',
  FAILED: 'failure',
  PENDING_FAILED: 'failure',
  SOME_FAILED: 'failure',
}

export function ChecksMiniStatusBoundary({children}: {children: React.ReactNode}) {
  const goToMergeBox = useGoToMergeBox()

  return (
    <MiniMergeStatusBoundary
      action={goToMergeBox}
      actionLabel="View checks"
      boundaryName="ChecksMiniStatusBoundary"
      text="Checks"
    >
      {children}
    </MiniMergeStatusBoundary>
  )
}

export function ChecksMiniStatus() {
  const data = useMergeabilityData()
  const goToMergeBox = useGoToMergeBox()

  const rollupSummary = data?.statusRollup.summary || []
  const status = data ? undefined : 'Checks unavailable'

  let successfulChecks = 0
  let totalChecks = 0

  for (const summary of rollupSummary) {
    totalChecks += summary.count

    if (isSuccessStatus(summary.state)) {
      successfulChecks += summary.count
    }
  }

  const pendingWorkflowCount = data?.statusRollup.pendingWorkflowApprovalRollup?.workflowsRequiringApprovalCount ?? 0
  const activeStateMinimum = data && data.statusRollup.combinedState !== 'PASSED' ? 1 : 0
  totalChecks = Math.max(totalChecks, pendingWorkflowCount, activeStateMinimum)

  const isUnavailable = Boolean(data) && totalChecks === 0
  const icon = data ? iconTypeByCombinedState[data.statusRollup.combinedState] : 'error'
  const accessibleLabel = data
    ? isUnavailable
      ? 'Checks 0: no checks available'
      : `Checks ${successfulChecks}/${totalChecks}`
    : 'Checks unavailable'

  return (
    <MiniMergeStatusItem aria-label="Checks">
      <MiniMergeStatusItem.LabelRow
        aria-label={isUnavailable ? undefined : `${accessibleLabel}: view status`}
        // TODO: Update the panel to open the checks section in the merge status panel. The current open panel options
        // don't support expanding sections within the merge status panel
        onClick={isUnavailable ? undefined : event => goToMergeBox(event.currentTarget)}
      >
        <MiniMergeStatusItem.Label
          icon={icon}
          isUnavailable={isUnavailable}
          status={status}
          successful={successfulChecks}
          total={totalChecks}
        >
          Checks
        </MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      <MiniMergeStatusItem.Progress
        aria-label={`${successfulChecks} of ${totalChecks} successful checks`}
        isUnavailable={isUnavailable}
        successful={successfulChecks}
        total={totalChecks}
        unavailableLabel="No checks available"
      />
    </MiniMergeStatusItem>
  )
}
